// src/data/coverage/surveyMeasureBindings.ts
// ============================================================
// SURVEY MODULE -> MEASURES IT INFORMS
//
// ─────────────────────────────────────────────────────────────────────────────
// THESE BINDINGS WERE INFERRED FROM MODULE NAMES, AND HAVE NOW BEEN CORRECTED AGAINST
// THE ACTUAL SEEDED QUESTIONS.
//
// The 37 seeded questions live in src/services/demoSurveyResponses.js (strategy 9,
// capabilities 14, readiness 14). Nothing in that file says which measure any question
// informs, so the original mapping was a judgement about what a module of that name would
// plausibly cover. Reading the questions found four concrete errors, all now fixed:
//
//   45 Change Management Capability   capabilities -> readiness
//        ready_13 asks change-management capacity. No capabilities question does.
//   35 De-Identification Needs        added to readiness
//        ready_7 asks de-identification capability. Nothing bound it before.
//   36 Regulatory Framework Alignment added to readiness
//        ready_8 asks regulatory alignment. Nothing bound it before.
//   39 Third-Party and Vendor Compliance  added to readiness
//        ready_10 asks vendor compliance maturity. Nothing bound it before.
//   38 Auditability and Reporting     REMOVED from readiness
//        No readiness question asks about auditability. It returns to library default.
//
// Three of those were measures NO module bound, so every coverage count that included
// them was wrong. The corrections move breadth, and that is the point: the numbers now
// describe what the questions actually ask.
//
// STILL INFERRED, and worth the next look: the measure-level mapping remains a judgement
// even where the module is right. No question carries a measure id, which is why the
// self-reported score in lib/coverage/surveyScoring is derived per MODULE rather than per
// measure, and why the override copy never claims a specific question said anything.
// ─────────────────────────────────────────────────────────────────────────────
// ============================================================

/** Module ids as keyed in src/services/demoSurveyResponses.js. */
export type SurveyModuleId = 'strategy' | 'capabilities' | 'readiness';

export interface SurveyModuleBinding {
  id: SurveyModuleId;
  /** Display label. Used verbatim inside the coverage source string. */
  label: string;
  /** Question count in the seeded set, for the record. Not used in any computation. */
  questionCount: number;
  measureIds: number[];
}

export const SURVEY_MODULE_BINDINGS: readonly SurveyModuleBinding[] = [
  {
    id: 'strategy',
    label: 'Strategic Priorities',
    questionCount: 9,
    measureIds: [5, 6, 7, 8, 21, 22, 24],
  },
  {
    id: 'capabilities',
    label: 'Data and Analytics Capabilities',
    questionCount: 14,
    // 45 moved to readiness: ready_13 asks change-management capacity, nothing here does.
    measureIds: [42, 43, 44, 46],
  },
  {
    id: 'readiness',
    label: 'Data Readiness and Governance',
    questionCount: 14,
    // +35 (ready_7), +36 (ready_8), +39 (ready_10), +45 (ready_13); -38, which no
    // question asks about. See the correction list in the header.
    measureIds: [26, 27, 28, 29, 30, 31, 32, 34, 35, 36, 39, 41, 45],
  },
] as const;

/** Lookup by module id. */
export const SURVEY_BINDING_BY_ID: Readonly<Record<SurveyModuleId, SurveyModuleBinding>> =
  SURVEY_MODULE_BINDINGS.reduce(
    (acc, b) => {
      acc[b.id] = b;
      return acc;
    },
    {} as Record<SurveyModuleId, SurveyModuleBinding>,
  );

/** Every measure any survey module touches. Deduplicated, ascending. */
export const ALL_SURVEY_MEASURE_IDS: readonly number[] = Array.from(
  new Set(SURVEY_MODULE_BINDINGS.flatMap((b) => b.measureIds)),
).sort((a, b) => a - b);
